/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['submitted', 'processing', 'shipped', 'returned', 'cancelled'] }] */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import cn from 'classnames';

import {
  CANCELLED,
  RETURNED
} from 'constants/orderStatuses';

import { updateOrderWithTrackingInfo } from 'actions/account/order';
import { isDesktop } from 'helpers/ClientUtils';
import { capitalize, groupBy, toArray } from 'helpers/lodashReplacement';
import {
  hasTracking,
  isItemInReturnState,
  isOrderCancellable,
  isOrderContainingShipped,
  isOrderReturnable
} from 'helpers/MyAccountUtils';
import {
  getItemStatusClassName
} from 'helpers/MyAccountRenderUtils';
import MartyLink from 'components/common/MartyLink';
import TrackingProgress from 'components/account/TrackingProgress';
import OrderProduct from 'components/account/Order/OrderProduct';
import css from 'styles/components/account/orderTable.scss';

export const isFitExempt = state => {
  switch (state) {
    case 'submitted':
    case 'processing':
    case 'cancelled':
      return true;
    default:
      return false;
  }
};

const makeReturnOrCancelColumn = (lineItem, onToggleItemSelect, orderId, testId) => {
  const {
    cancellable,
    returnable,
    state,
    itemUniqueId
  } = lineItem;
  const ariaItemId = `aria-orderProductDetails-${itemUniqueId}`;

  if (returnable) {
    return (
      <td className={css.state}>
        <input
          type="checkbox"
          id={`return-${itemUniqueId}`}
          onChange={onToggleItemSelect.bind(this, lineItem, orderId)}
          data-test-id={testId('returnInput')}
          aria-describedby={ariaItemId} />
        <label htmlFor={`return-${itemUniqueId}`} aria-label="return"/>
      </td>
    );
  } else if (state === RETURNED) {
    return <td className={css.state} data-test-id={testId('returned')}>Returned</td>;
  }

  if (cancellable) {
    return (
      <td className={css.state}>
        <input
          type="checkbox"
          id={`cancel-${itemUniqueId}`}
          onChange={onToggleItemSelect.bind(this, lineItem, orderId)}
          data-test-id={testId('cancelInput')}
          aria-describedby={ariaItemId} />
        <label htmlFor={`cancel-${itemUniqueId}`} aria-label="cancel"/>
      </td>
    );
  } else if (state === CANCELLED) {
    return <td className={css.state} data-test-id={testId('cancelled')}>Cancelled</td>;
  }

  return <td data-test-id={testId('permanentSale')}/>;
};

const fitTypes = [
  { val: '0', name: 'Perfect' },
  { val: '1', name: 'Too Big' },
  { val: '-1', name: 'Too Small' }
];

export const FitSurveyOptions = ({ indicator, onSelectFitQuestion, isFitSurveyLoading = false }) => fitTypes.map(type => {
  const { val, name } = type;
  const selected = (indicator && indicator === val);
  return (
    <button
      key={val}
      onClick={onSelectFitQuestion}
      disabled={isFitSurveyLoading}
      className={cn({ [css.loadingButton]: isFitSurveyLoading })}
      type="button"
      data-selected={selected}
      value={val}>{name}</button>
  );
});

export const makeTrackingProgressBar = ({ latestArrivalDate, order }) => (
  <div className={css.trackingProgress}>
    <TrackingProgress order={order} estimatedDeliveryDate={latestArrivalDate}/>
  </div>
);

export const makeTrackingLink = ({ shipmentId, trackingNumber, orderId, testId }) => (
  <div className={css.trackPackage}>
      TRACK: <Link to={`/shipments/${orderId}/${shipmentId}`} data-test-id={testId('trackingLink')}>{trackingNumber}</Link>
  </div>
);

export class OrderTableLayout extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    marketplace: PropTypes.object
  }

  static propTypes = {
    order: PropTypes.object.isRequired,
    onToggleItemSelect: PropTypes.func.isRequired
  }

  componentDidUpdate(prevProps) {
    const { marketplace } = this.context;

    // only fire this tracking for mobile views
    if (!isDesktop(marketplace)) {
      const {
        order,
        shipmentTracking: { trackingInfo },
        updateOrderWithTrackingInfo
      } = this.props;

      const { shipmentTracking: { trackingInfo: prevTrackingInfo } } = prevProps;

      if (trackingInfo.length !== prevTrackingInfo.length) {
        updateOrderWithTrackingInfo(order, trackingInfo);
      }
    }
  }

  render () {
    const {
      testId,
      marketplace: {
        desktopBaseUrl,
        account: {
          showReturnLabelLink,
          showExchangeLink,
          hideAllReturns
        }
      }
    } = this.context;

    const { order, onToggleItemSelect, onSelectFitQuestion, fitItems, showZenReturns, showFitSurvey, isFitSurveyLoading } = this.props;

    const { orderId, lineItems, legacy } = order;
    const shouldBeNonReturnable = showZenReturns ? !showZenReturns : hideAllReturns; // if we are shwoing zen returns we want the opposite, so the returnable state is viewable
    const itemsByShipmentId = toArray(groupBy(lineItems, lineItem => lineItem.shipment.id));

    return (
      <div className={css.container}>
        <div>
          {itemsByShipmentId.map(shipmentIdItems => {
            const { itemUniqueId, shipment: { id: shipmentId, latestArrivalDate, trackingNumber } } = shipmentIdItems[0];

            return (
              <div key={itemUniqueId} data-test-id={testId('orderTableRow')}>
                {isOrderContainingShipped(shipmentIdItems) && (
                <>
                  {hasTracking(order) && makeTrackingProgressBar({ latestArrivalDate, order })}
                  {trackingNumber && !legacy && makeTrackingLink({ shipmentId, trackingNumber, orderId, testId })}
                </>
                )}
              </div>
            );
          })}
        </div>

        <h2>Items Summary</h2>

        <table className={css.orderItems}>
          <caption>List of items ordered</caption>
          <thead>
            <tr>
              {isOrderReturnable(lineItems, shouldBeNonReturnable) && <th className={css.state}>Return</th>}
              {isOrderCancellable(lineItems) && <th className={css.state}>Cancel</th>}
              <th className={css.items}>Items</th>
              <th className={css.status}>Status</th>
              { showFitSurvey && <th className={css.fit} /> }
              <th className={css.price}>Price</th>
            </tr>
          </thead>

          <tbody>
            {lineItems.map(lineItem => {
              const {
                product,
                state,
                amount,
                finalSale,
                giftCard,
                shipment: { id: shipmentId, trackingNumber },
                legacy,
                returnLabelUrl,
                exchangeable,
                itemUniqueId
              } = lineItem;
              const { stockId, asin, name } = product;
              const { indicator, id } = fitItems.find(item => item.asin === asin) || {};

              const isFitSurveyExempt = (giftCard || isFitExempt(state));

              return (
                <tr key={itemUniqueId} className={css.item} data-test-id={testId('orderTableRow')}>
                  {(isOrderReturnable(lineItems, shouldBeNonReturnable) || isOrderCancellable(lineItems)) && (
                    makeReturnOrCancelColumn(lineItem, onToggleItemSelect, orderId, testId)
                  )}

                  <td className={css.items}>
                    <OrderProduct
                      finalSale={finalSale}
                      product={product}
                      isLegacy={legacy}
                      lineItemState={state}
                      itemUniqueId={itemUniqueId}/>
                  </td>

                  <td className={css.status}>
                    <span className={getItemStatusClassName(state)} data-test-id={testId('itemStatus')}>{capitalize(state)}</span>
                    {trackingNumber && !legacy && !isItemInReturnState(state) && (
                      <div className={css.tracking}>
                        <MartyLink to={`/shipments/${orderId}/${shipmentId}`} data-test-id={testId('itemTrackingLink')}>
                        Track this shipment <span>{name} with UPS tracking number</span>
                          <p>{trackingNumber}</p>
                        </MartyLink>
                      </div>
                    )}
                    {showReturnLabelLink && returnLabelUrl && (
                      <div>
                        <a
                          className={css.displayLabel}
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`${desktopBaseUrl}${returnLabelUrl}&displayInfo=true`}>Display Your Return Label</a>
                      </div>
                    )}
                    {showExchangeLink && exchangeable && (
                      <div className={css.exchange}>
                        <a href={`${desktopBaseUrl}/xoInitiate?orderId=${orderId}&asin=${asin}`}>Exchange this Item</a>
                      </div>
                    )}
                  </td>

                  { showFitSurvey &&
                    <td
                      className={css.fit}
                      data-stockid={stockId}
                      data-asin={asin}
                      data-id={id}
                    >
                      { !isFitSurveyExempt &&
                        <>
                          <p>How was the fit?</p>
                          <FitSurveyOptions onSelectFitQuestion={onSelectFitQuestion} indicator={indicator} isFitSurveyLoading={isFitSurveyLoading} />
                        </>
                      }
                    </td>
                  }

                  <td className={css.price} data-test-id={testId('itemPrice')}>
                    {amount}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  order: state.order.order,
  shipmentTracking: state.shipmentTracking
});

export default connect(mapStateToProps, {
  updateOrderWithTrackingInfo
})(OrderTableLayout);
